import { Button, Text } from '@chakra-ui/react';

import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { Badge, BadgeStatus } from '@aslanpay/portal-common-lib';

import { SalaryReportRowStatusValues } from 'app/constants/salaryReconciliations';

import { SalaryReconciliationReportRow } from 'app/types/salaryReconciliations';

import { formatReconciliationAmount } from 'app/utils/reconciliations';
import { formatDiscrepancyReason, renderFixability } from 'app/utils/salaryReconciliations';

interface SalaryReconciliationReportColumnsOptions {
  showCompanyColumn?: boolean;
  onResolve?: (row: SalaryReconciliationReportRow) => void;
  fixedDiscrepancyIds?: Set<string>;
}

/**
 * @description Columns of a salary accrual reconciliation run's report. The company column is left out in a company's
 * own view. A problem still open has a Resolve action; one marked as fixed here, or no longer open, says so instead.
 */
function getSalaryReconciliationReportColumns({
  showCompanyColumn = true,
  onResolve = undefined,
  fixedDiscrepancyIds = new Set<string>(),
}: SalaryReconciliationReportColumnsOptions = {}) {
  const columnHelper = createColumnHelper<SalaryReconciliationReportRow>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<SalaryReconciliationReportRow, any>[] = [
    columnHelper.accessor('status', {
      header: 'Status',
      enableSorting: false,
      cell: (info) => {
        const status = SalaryReportRowStatusValues[info.row.original.status];
        return (
          <Badge
            value={status?.value ?? info.getValue()}
            status={(status?.color ?? 'created') as BadgeStatus}
          />
        );
      },
    }),
    columnHelper.accessor('companyName', {
      id: 'company',
      header: 'Company',
      enableSorting: false,
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('individualName', {
      header: 'Person',
      enableSorting: false,
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('externalPayrollId', {
      header: 'Payroll ID',
      enableSorting: false,
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('reason', {
      header: 'Reason',
      enableSorting: false,
      cell: (info) => (
        <Text minW="200px" whiteSpace="normal">
          {formatDiscrepancyReason(info.getValue())}
        </Text>
      ),
    }),
    columnHelper.accessor('expectedAmount', {
      header: 'Expected',
      enableSorting: false,
      cell: (info) => formatReconciliationAmount(info.getValue()),
    }),
    columnHelper.accessor('actualAmount', {
      header: 'Actual',
      enableSorting: false,
      cell: (info) => formatReconciliationAmount(info.getValue()),
    }),
    columnHelper.accessor('difference', {
      header: 'Difference',
      enableSorting: false,
      cell: (info) => formatReconciliationAmount(info.getValue()),
    }),
    columnHelper.accessor('fixability', {
      header: 'Would an adjustment fix it',
      enableSorting: false,
      cell: (info) => renderFixability(info.getValue()),
    }),
    columnHelper.accessor('daysOpen', {
      header: 'Days open',
      enableSorting: false,
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      cell: (info) => {
        const { discrepancyId } = info.row.original;
        if (fixedDiscrepancyIds.has(discrepancyId)) {
          return <Text color="brand.darkGray">Marked as fixed</Text>;
        }
        if (info.row.original.stillOpen) {
          return (
            <Button
              size="xs"
              variant="outline"
              onClick={(event) => {
                event.stopPropagation();
                onResolve?.(info.row.original);
              }}
              data-testid={`resolve-${discrepancyId}`}
            >
              Resolve
            </Button>
          );
        }
        return <Text color="brand.darkGray">No longer open</Text>;
      },
    }),
  ];

  return columns.filter(
    (column) =>
      (showCompanyColumn || column.id !== 'company') &&
      (onResolve !== undefined || column.id !== 'actions')
  );
}

export default getSalaryReconciliationReportColumns;
